import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Save, Eye, ArrowLeft, AlertCircle, CheckCircle, FileText, Download, Sparkles, X } from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import AdminNavbar from '../components/admin/AdminNavbar';
import SEOHead from '../components/SEOHead';
import ImageUploadDropzone from '../components/ImageUploadDropzone';
import { useAuth } from '../contexts/AuthContext';
import { supabase, BlogPost } from '../utils/supabase';
import { stripAllFormatting, normalizeListFormatting } from '../utils/contentSanitizer';
import { markdownToHTML } from '../utils/markdownConverter';
import { contentTemplates } from '../utils/contentTemplates';

const AdminPostEditorPage = () => {
  const { isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isNewPost = id === 'new';
  const linkedinFieldRef = useRef<HTMLDivElement>(null);

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    image_url: '',
    author: 'CTC Waterworks',
    status: 'draft' as 'draft' | 'published',
    tags: [] as string[],
    date: new Date().toISOString().split('T')[0],
    linkedin_url: ''
  });

  const [tagInput, setTagInput] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [isLoading, setIsLoading] = useState(!isNewPost);
  const [showMarkdownModal, setShowMarkdownModal] = useState(false);
  const [markdownInput, setMarkdownInput] = useState('');
  const [showTemplateDropdown, setShowTemplateDropdown] = useState(false);
  const quillRef = useRef<ReactQuill>(null);
  const [imageAltText, setImageAltText] = useState('');
  const [showImageModal, setShowImageModal] = useState(false);
  const [pendingImageFile, setPendingImageFile] = useState<File | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [openInNewTab, setOpenInNewTab] = useState(false);
  const [highlightLinkedIn, setHighlightLinkedIn] = useState(false);

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/admin');
    }
  }, [isAuthenticated, loading, navigate]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (quillRef.current) {
        const quill = quillRef.current.getEditor();
        const toolbar = quill.getModule('toolbar');
        toolbar.addHandler('image', handleImageClick);
        toolbar.addHandler('link', handleLinkClick);
      }
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (isAuthenticated && !isNewPost && id) {
      fetchPost();
    }
  }, [isAuthenticated, isNewPost, id]);

  useEffect(() => {
    const focusParam = searchParams.get('focus');
    if (focusParam === 'linkedin' && linkedinFieldRef.current && !isLoading) {
      setTimeout(() => {
        linkedinFieldRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
        setHighlightLinkedIn(true);
        setTimeout(() => setHighlightLinkedIn(false), 3000);
      }, 300);
    }
  }, [searchParams, isLoading]);

  const fetchPost = async () => {
    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('id', id)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        const dateValue = data.date ? new Date(data.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0];

        setFormData({
          title: data.title,
          slug: data.slug,
          excerpt: data.excerpt,
          content: data.content,
          image_url: data.image_url,
          author: data.author,
          status: data.status,
          tags: data.tags || [],
          date: dateValue,
          linkedin_url: data.linkedin_url || ''
        });
      } else {
        navigate('/admin/posts');
      }
    } catch (error) {
      console.error('Error fetching post:', error);
      setSaveMessage({ type: 'error', text: 'Failed to load post' });
    } finally {
      setIsLoading(false);
    }
  };

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleTitleChange = (title: string) => {
    setFormData(prev => ({
      ...prev,
      title,
      slug: generateSlug(title)
    }));
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleSave = async (status: 'draft' | 'published') => {
    if (!formData.title.trim() || !formData.content.trim()) {
      setSaveMessage({ type: 'error', text: 'Title and content are required' });
      return;
    }

    if (!formData.date || !/^\d{4}-\d{2}-\d{2}$/.test(formData.date)) {
      setSaveMessage({ type: 'error', text: 'Valid publication date is required (YYYY-MM-DD format)' });
      return;
    }

    setIsSaving(true);
    setSaveMessage(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      console.log('[AdminPostEditor] Auth session during save:', {
        hasSession: !!session,
        userId: session?.user?.id,
        userEmail: session?.user?.email
      });

      const postData: any = {
        ...formData,
        status,
        slug: formData.slug || generateSlug(formData.title),
        excerpt: formData.excerpt || formData.content.replace(/<[^>]*>/g, '').substring(0, 200),
        date: formData.date
      };

      console.log('[AdminPostEditor] Saving post with status:', status);
      console.log('[AdminPostEditor] Full post data:', postData);

      if (isNewPost) {
        const { data: insertedData, error } = await supabase
          .from('blog_posts')
          .insert([postData])
          .select();

        console.log('[AdminPostEditor] Insert result:', { insertedData, error });

        if (error) {
          console.error('[AdminPostEditor] Insert error:', {
            message: error.message,
            details: error.details,
            hint: error.hint,
            code: error.code
          });
          throw error;
        }

        setSaveMessage({ type: 'success', text: `Post ${status === 'published' ? 'published' : 'saved as draft'} successfully!` });
        setTimeout(() => navigate('/admin/posts'), 1500);
      } else {
        const { error } = await supabase
          .from('blog_posts')
          .update(postData)
          .eq('id', id);

        if (error) throw error;

        setSaveMessage({ type: 'success', text: `Post ${status === 'published' ? 'published' : 'updated'} successfully!` });
        setFormData(prev => ({ ...prev, status }));
      }
    } catch (error: any) {
      console.error('Error saving post:', error);
      setSaveMessage({
        type: 'error',
        text: error.message || 'Failed to save post. Please try again.'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handlePreview = () => {
    localStorage.setItem('blog_preview', JSON.stringify(formData));
    window.open('/blog/preview', '_blank');
  };

  const handlePasteClean = () => {
    if (formData.content) {
      const cleaned = stripAllFormatting(formData.content);
      const normalized = normalizeListFormatting(cleaned);
      setFormData(prev => ({ ...prev, content: normalized }));
      setSaveMessage({ type: 'success', text: 'Content formatting cleaned!' });
      setTimeout(() => setSaveMessage(null), 3000);
    }
  };

  const handleImportMarkdown = () => {
    if (markdownInput.trim()) {
      const html = markdownToHTML(markdownInput);
      setFormData(prev => ({ ...prev, content: html }));
      setMarkdownInput('');
      setShowMarkdownModal(false);
      setSaveMessage({ type: 'success', text: 'Markdown imported successfully!' });
      setTimeout(() => setSaveMessage(null), 3000);
    }
  };

  const handleInsertTemplate = (templateIndex: number) => {
    const template = contentTemplates[templateIndex];
    setFormData(prev => ({ ...prev, content: template.content }));
    setShowTemplateDropdown(false);
    setSaveMessage({ type: 'success', text: `${template.name} template inserted!` });
    setTimeout(() => setSaveMessage(null), 3000);
  };

  const handleImageClick = () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files?.[0];
      if (file) {
        setPendingImageFile(file);
        setImageAltText('');
        setShowImageModal(true);
      }
    };
  };

  const handleInsertImage = async () => {
    if (!pendingImageFile) return;

    try {
      setIsUploadingImage(true);

      if (!pendingImageFile.type.startsWith('image/')) {
        throw new Error('Please upload an image file');
      }

      if (pendingImageFile.size > 5 * 1024 * 1024) {
        throw new Error('Image must be less than 5MB');
      }

      const fileExt = pendingImageFile.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('blog-images')
        .upload(filePath, pendingImageFile, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('blog-images')
        .getPublicUrl(filePath);

      const quill = quillRef.current?.getEditor();
      if (quill) {
        const range = quill.getSelection(true);
        quill.insertEmbed(range.index, 'image', publicUrl);

        const imageElement = quill.root.querySelector(`img[src="${publicUrl}"]`);
        if (imageElement && imageAltText.trim()) {
          imageElement.setAttribute('alt', imageAltText.trim());
        }

        quill.setSelection(range.index + 1);
      }

      setShowImageModal(false);
      setPendingImageFile(null);
      setImageAltText('');
      setSaveMessage({ type: 'success', text: 'Image uploaded successfully!' });
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (error: any) {
      console.error('Upload error:', error);
      setSaveMessage({ type: 'error', text: error.message || 'Failed to upload image' });
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleLinkClick = () => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    const range = quill.getSelection();
    if (range) {
      const selectedText = quill.getText(range.index, range.length);
      const format = quill.getFormat(range);

      setLinkText(selectedText || '');
      setLinkUrl(format.link || '');
      setOpenInNewTab(false);
      setShowLinkModal(true);
    }
  };

  const handleInsertLink = () => {
    if (!linkUrl.trim()) {
      setSaveMessage({ type: 'error', text: 'Please enter a URL' });
      return;
    }

    let url = linkUrl.trim();
    if (!url.startsWith('http://') && !url.startsWith('https://') && !url.startsWith('mailto:')) {
      url = 'https://' + url;
    }

    const quill = quillRef.current?.getEditor();
    if (quill) {
      const range = quill.getSelection(true);

      if (range.length > 0) {
        quill.formatText(range.index, range.length, 'link', url);
      } else if (linkText.trim()) {
        quill.insertText(range.index, linkText, 'link', url);
        quill.setSelection(range.index + linkText.length);
      } else {
        quill.insertText(range.index, url, 'link', url);
        quill.setSelection(range.index + url.length);
      }
    }

    setShowLinkModal(false);
    setLinkUrl('');
    setLinkText('');
    setOpenInNewTab(false);
    setSaveMessage({ type: 'success', text: 'Link inserted successfully!' });
    setTimeout(() => setSaveMessage(null), 3000);
  };

  const quillModules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
        [{ 'font': [] }],
        [{ 'size': ['small', false, 'large', 'huge'] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'script': 'sub' }, { 'script': 'super' }],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
        [{ 'align': [] }, { 'align': 'center' }, { 'align': 'right' }, { 'align': 'justify' }],
        ['blockquote', 'code-block'],
        ['link', 'image', 'video'],
        ['clean']
      ],
      handlers: {
        image: handleImageClick,
        link: handleLinkClick
      }
    },
    clipboard: {
      matchVisual: false
    }
  }), []);

  const quillFormats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'script',
    'list', 'bullet', 'indent',
    'align',
    'blockquote', 'code-block',
    'link', 'image', 'video'
  ];

  if (loading || isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-ctc-blue"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead
        title={`${isNewPost ? 'New Post' : 'Edit Post'} - Blog Admin`}
        description="Create or edit blog post"
        noIndex={true}
      />
      <AdminNavbar />

      <div className="pt-24 pb-16 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/admin/posts')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft size={24} />
              </button>
              <div>
                <h1 className="text-4xl font-bold text-gray-900">
                  {isNewPost ? 'New Post' : 'Edit Post'}
                </h1>
                <p className="text-gray-600 mt-1">
                  {isNewPost ? 'Create a new blog post' : 'Update your blog post'}
                </p>
              </div>
            </div>
          </div>

          {saveMessage && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`mb-6 rounded-lg p-4 flex items-start space-x-3 ${
                saveMessage.type === 'success'
                  ? 'bg-green-50 border border-green-200'
                  : 'bg-red-50 border border-red-200'
              }`}
            >
              {saveMessage.type === 'success' ? (
                <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
              ) : (
                <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
              )}
              <p className={saveMessage.type === 'success' ? 'text-green-800' : 'text-red-800'}>
                {saveMessage.text}
              </p>
            </motion.div>
          )}

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-ctc-blue focus:border-transparent transition-colors text-lg font-semibold"
                  placeholder="Enter post title"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  URL Slug
                </label>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-ctc-blue focus:border-transparent transition-colors font-mono text-sm"
                  placeholder="post-url-slug"
                />
                <p className="text-sm text-gray-500 mt-1">
                  URL: /blog/{formData.slug || 'post-slug'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Featured Image
                </label>
                <ImageUploadDropzone
                  currentImageUrl={formData.image_url}
                  onImageUploaded={(url) => setFormData(prev => ({ ...prev, image_url: url }))}
                />
                <div className="mt-2">
                  <label className="block text-xs text-gray-500 mb-1">
                    Or paste URL:
                  </label>
                  <input
                    type="text"
                    value={formData.image_url}
                    onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
                    className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-ctc-blue focus:border-transparent transition-colors"
                    placeholder="https://example.com/image.jpg"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Excerpt
                </label>
                <textarea
                  value={formData.excerpt}
                  onChange={(e) => setFormData(prev => ({ ...prev, excerpt: e.target.value }))}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-ctc-blue focus:border-transparent transition-colors resize-none"
                  rows={3}
                  placeholder="Short description (will be auto-generated from content if left empty)"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Content *
                  </label>
                  <div className="flex items-center space-x-2">
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setShowTemplateDropdown(!showTemplateDropdown)}
                        className="flex items-center space-x-2 px-3 py-1.5 text-sm bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors border border-purple-200"
                      >
                        <Sparkles size={16} />
                        <span>Templates</span>
                      </button>
                      {showTemplateDropdown && (
                        <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 z-50">
                          <div className="py-1">
                            {contentTemplates.map((template, index) => (
                              <button
                                key={index}
                                onClick={() => handleInsertTemplate(index)}
                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                              >
                                {template.name}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowMarkdownModal(true)}
                      className="flex items-center space-x-2 px-3 py-1.5 text-sm bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors border border-blue-200"
                    >
                      <Download size={16} />
                      <span>Import Markdown</span>
                    </button>
                    <button
                      type="button"
                      onClick={handlePasteClean}
                      disabled={!formData.content}
                      className="flex items-center space-x-2 px-3 py-1.5 text-sm bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors border border-green-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FileText size={16} />
                      <span>Clean Formatting</span>
                    </button>
                  </div>
                </div>
                <div className="border border-gray-300 rounded-lg overflow-hidden quill-editor-container">
                  <ReactQuill
                    ref={quillRef}
                    theme="snow"
                    value={formData.content}
                    onChange={(content) => setFormData(prev => ({ ...prev, content }))}
                    modules={quillModules}
                    formats={quillFormats}
                    className="bg-white"
                    style={{ minHeight: '500px' }}
                  />
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  Use templates for structured content, import markdown, or clean pasted formatting with the tools above.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Author
                  </label>
                  <input
                    type="text"
                    value={formData.author}
                    onChange={(e) => setFormData(prev => ({ ...prev, author: e.target.value }))}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-ctc-blue focus:border-transparent transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Publication Date *
                  </label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-ctc-blue focus:border-transparent transition-colors"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tags
                </label>
                <div className="flex space-x-2 mb-3">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                    className="flex-1 px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-ctc-blue focus:border-transparent transition-colors"
                    placeholder="Add a tag"
                  />
                  <button
                    onClick={handleAddTag}
                    className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                  >
                    Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center px-3 py-1 bg-ctc-blue/10 text-ctc-blue rounded-full text-sm"
                    >
                      {tag}
                      <button
                        onClick={() => handleRemoveTag(tag)}
                        className="ml-2 text-ctc-blue hover:text-ctc-blue-dark"
                      >
                        ✕
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              <div
                ref={linkedinFieldRef}
                className={`transition-all duration-300 ${
                  highlightLinkedIn ? 'ring-4 ring-blue-300 rounded-lg p-4 -m-4 bg-blue-50' : ''
                }`}
              >
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  LinkedIn Post URL
                </label>
                <input
                  type="url"
                  value={formData.linkedin_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, linkedin_url: e.target.value }))}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-ctc-blue focus:border-transparent transition-colors"
                  placeholder="https://www.linkedin.com/posts/..."
                />
                <p className="text-sm text-gray-500 mt-1">
                  Link to the specific LinkedIn post for the "Discuss on LinkedIn" button. Leave empty to use CTC's LinkedIn page.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-8 flex items-center justify-between">
            <button
              onClick={handlePreview}
              className="flex items-center space-x-2 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            >
              <Eye size={20} />
              <span>Preview</span>
            </button>

            <div className="flex items-center space-x-4">
              <button
                onClick={() => handleSave('draft')}
                disabled={isSaving}
                className="flex items-center space-x-2 px-6 py-3 border border-ctc-blue text-ctc-blue rounded-lg hover:bg-ctc-blue/5 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save size={20} />
                <span>{isSaving ? 'Saving...' : 'Save Draft'}</span>
              </button>

              <button
                onClick={() => handleSave('published')}
                disabled={isSaving}
                className="flex items-center space-x-2 px-6 py-3 bg-ctc-blue text-white rounded-lg hover:bg-ctc-blue-dark transition-colors font-medium shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save size={20} />
                <span>{isSaving ? 'Publishing...' : 'Publish'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {showMarkdownModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-6"
            onClick={() => setShowMarkdownModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[80vh] flex flex-col"
            >
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Import Markdown</h2>
                  <p className="text-sm text-gray-600 mt-1">Paste your markdown content below</p>
                </div>
                <button
                  onClick={() => setShowMarkdownModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-6">
                <textarea
                  value={markdownInput}
                  onChange={(e) => setMarkdownInput(e.target.value)}
                  className="w-full h-96 px-4 py-3 font-mono text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-ctc-blue focus:border-transparent resize-none"
                  placeholder="# Your Markdown Here

## Section One
This is a paragraph with **bold** and *italic* text.

- List item one
- List item two

> This is a blockquote

[Link text](https://example.com)"
                />
                <p className="text-sm text-gray-500 mt-3">
                  Supported: headings (##, ###), bold (**text**), italic (*text*), lists (-, *, 1.), blockquotes (&gt;), links, and horizontal rules (---).
                </p>
              </div>

              <div className="flex items-center justify-end space-x-4 p-6 border-t border-gray-200">
                <button
                  onClick={() => setShowMarkdownModal(false)}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleImportMarkdown}
                  disabled={!markdownInput.trim()}
                  className="px-6 py-3 bg-ctc-blue text-white rounded-lg hover:bg-ctc-blue-dark transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Import Content
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showImageModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-6"
            onClick={() => !isUploadingImage && setShowImageModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-xl shadow-2xl max-w-md w-full"
            >
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Insert Image</h2>
                  <p className="text-sm text-gray-600 mt-1">Add alt text for better SEO and accessibility</p>
                </div>
                {!isUploadingImage && (
                  <button
                    onClick={() => setShowImageModal(false)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X size={24} />
                  </button>
                )}
              </div>

              <div className="p-6 space-y-4">
                {pendingImageFile && (
                  <div className="rounded-lg overflow-hidden border border-gray-200">
                    <img
                      src={URL.createObjectURL(pendingImageFile)}
                      alt="Preview"
                      className="w-full h-48 object-cover"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Alt Text (Recommended)
                  </label>
                  <input
                    type="text"
                    value={imageAltText}
                    onChange={(e) => setImageAltText(e.target.value)}
                    placeholder="Describe the image for screen readers and SEO"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ctc-blue focus:border-transparent"
                    disabled={isUploadingImage}
                    onKeyPress={(e) => e.key === 'Enter' && handleInsertImage()}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Example: "A water filtration system installed in an industrial facility"
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-end space-x-4 p-6 border-t border-gray-200">
                <button
                  onClick={() => setShowImageModal(false)}
                  disabled={isUploadingImage}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={handleInsertImage}
                  disabled={isUploadingImage}
                  className="px-6 py-3 bg-ctc-blue text-white rounded-lg hover:bg-ctc-blue-dark transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {isUploadingImage && (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  )}
                  <span>{isUploadingImage ? 'Uploading...' : 'Insert Image'}</span>
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showLinkModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-6"
            onClick={() => setShowLinkModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-xl shadow-2xl max-w-md w-full"
            >
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Insert Link</h2>
                  <p className="text-sm text-gray-600 mt-1">Add a hyperlink to your content</p>
                </div>
                <button
                  onClick={() => setShowLinkModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    URL *
                  </label>
                  <input
                    type="text"
                    value={linkUrl}
                    onChange={(e) => setLinkUrl(e.target.value)}
                    placeholder="https://example.com"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ctc-blue focus:border-transparent"
                    onKeyPress={(e) => e.key === 'Enter' && handleInsertLink()}
                    autoFocus
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Enter the full URL including https://
                  </p>
                </div>

                {!linkText && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Link Text (Optional)
                    </label>
                    <input
                      type="text"
                      value={linkText}
                      onChange={(e) => setLinkText(e.target.value)}
                      placeholder="Click here"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ctc-blue focus:border-transparent"
                      onKeyPress={(e) => e.key === 'Enter' && handleInsertLink()}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Leave empty to use the URL as the link text
                    </p>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end space-x-4 p-6 border-t border-gray-200">
                <button
                  onClick={() => setShowLinkModal(false)}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleInsertLink}
                  disabled={!linkUrl.trim()}
                  className="px-6 py-3 bg-ctc-blue text-white rounded-lg hover:bg-ctc-blue-dark transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Insert Link
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        .ql-toolbar {
          position: sticky;
          top: 0;
          z-index: 10;
          background: white;
          border-top-left-radius: 0.5rem;
          border-top-right-radius: 0.5rem;
        }
        .ql-container {
          font-size: 1.0625rem;
          min-height: 500px;
          max-height: 600px;
          overflow-y: auto;
        }
        .ql-editor {
          min-height: 500px;
          line-height: 1.7;
          color: #1f2937;
        }

        /* Toolbar tooltips */
        .ql-toolbar button {
          position: relative;
        }
        .ql-toolbar .ql-bold:hover::after { content: "Bold"; }
        .ql-toolbar .ql-italic:hover::after { content: "Italic"; }
        .ql-toolbar .ql-underline:hover::after { content: "Underline"; }
        .ql-toolbar .ql-strike:hover::after { content: "Strikethrough"; }
        .ql-toolbar .ql-blockquote:hover::after { content: "Blockquote"; }
        .ql-toolbar .ql-code-block:hover::after { content: "Code Block"; }
        .ql-toolbar .ql-link:hover::after { content: "Insert Link"; }
        .ql-toolbar .ql-image:hover::after { content: "Insert Image"; }
        .ql-toolbar .ql-video:hover::after { content: "Insert Video"; }
        .ql-toolbar .ql-clean:hover::after { content: "Remove Formatting"; }
        .ql-toolbar .ql-list[value="ordered"]:hover::after { content: "Numbered List"; }
        .ql-toolbar .ql-list[value="bullet"]:hover::after { content: "Bullet List"; }
        .ql-toolbar .ql-indent[value="-1"]:hover::after { content: "Decrease Indent"; }
        .ql-toolbar .ql-indent[value="+1"]:hover::after { content: "Increase Indent"; }
        .ql-toolbar .ql-script[value="sub"]:hover::after { content: "Subscript"; }
        .ql-toolbar .ql-script[value="super"]:hover::after { content: "Superscript"; }
        .ql-toolbar button.ql-align:not([class*="ql-active"]):hover::after { content: "Align Left"; }
        .ql-toolbar button.ql-align[value="center"]:hover::after { content: "Align Center"; }
        .ql-toolbar button.ql-align[value="right"]:hover::after { content: "Align Right"; }
        .ql-toolbar button.ql-align[value="justify"]:hover::after { content: "Align Justify"; }

        .ql-toolbar button:hover::after {
          position: absolute;
          bottom: -35px;
          left: 50%;
          transform: translateX(-50%);
          background: #1f2937;
          color: white;
          padding: 6px 10px;
          border-radius: 4px;
          font-size: 12px;
          white-space: nowrap;
          z-index: 1000;
          pointer-events: none;
        }

        .ql-editor img {
          max-width: 100%;
          height: auto;
          display: block;
          margin: 1rem 0;
        }
        .ql-editor h2 {
          font-size: 1.75rem;
          font-weight: 700;
          margin-top: 2.5rem;
          margin-bottom: 0.75rem;
          line-height: 1.3;
          color: #111827;
          letter-spacing: -0.02em;
        }
        .ql-editor h3 {
          font-size: 1.375rem;
          font-weight: 600;
          margin-top: 2rem;
          margin-bottom: 0.5rem;
          line-height: 1.4;
          color: #1f2937;
          letter-spacing: -0.015em;
        }
        .ql-editor p {
          margin-bottom: 1rem;
          line-height: 1.7;
          color: #374151;
        }
        .ql-editor ul,
        .ql-editor ol {
          margin: 1.25rem 0;
          padding-left: 1.75rem;
        }
        .ql-editor li {
          margin-bottom: 0.375rem;
          line-height: 1.7;
          color: #374151;
        }
        .ql-editor li + li {
          margin-top: 0.375rem;
        }
        .ql-editor blockquote {
          border-left: 3px solid #0066cc;
          padding: 1rem 1.25rem;
          margin: 1.5rem 0;
          font-style: italic;
          color: #4b5563;
          background-color: #f9fafb;
          border-radius: 0 0.375rem 0.375rem 0;
        }
        .ql-editor strong {
          font-weight: 600;
          color: #111827;
        }
        .ql-editor hr {
          margin: 2rem 0;
          border: none;
          border-top: 1px solid #e5e7eb;
        }
        .ql-editor a {
          color: #0066cc;
          text-decoration: underline;
          text-underline-offset: 2px;
        }
      `}</style>
    </div>
  );
};

export default AdminPostEditorPage;
